import { AutomatedElements, ComponentConfigModel, CONFIG_OPTIONS, ElementsResponseModel, ImageLetterModel, LetterS3Model, LetterSectionModel, LetterShareStatus, RegExpModel, TemplateContentModel } from 'countable@model';
import { LETTER_SIGNATURES } from '../services/eng-letter-constants';
import { GenerateRandomUtils } from './random-utils';
import { CLIENT_IMAGES_FOLDERS } from './stored-procedures.constant';

export class LetterUtil {

  static mapElementsResponseToValue(elements: any, firmName: string, datePicker: string, engLetterDate: string) {
    const parsedElements: ElementsResponseModel = elements ? JSON.parse(elements)[0] : null;
    if(parsedElements) {
      return {
        addBoDir: parsedElements?.addressBoardOfDirectors ?? '',
        addManagement: parsedElements?.addressManagement ?? '',
        addShare: parsedElements?.addressShareholders ?? '',
        cityProvince: parsedElements?.cityAndProvince ?? '',
        clientCity: parsedElements?.clientCity ?? '',
        addComplete: parsedElements?.completeAddress ?? '',
        entityName: parsedElements?.entityName ?? '',
        yearEndCurr: parsedElements?.financialYearEnddate ?? '',
        yearEndPrev: parsedElements?.previousYearEndDate ?? '',
        balanceSheet: parsedElements?.balanceSheet ?? '',
        cityState: parsedElements?.cityAndState ?? '',
        firstName: parsedElements?.firstName ?? '',
        lastName: parsedElements?.lastName ?? '',
        postalCode: parsedElements?.postCode ?? '',
        date: '',
        firmName: firmName,
        firmAddress: parsedElements?.firmAddress ?? '',
        partnerName: parsedElements?.partnerName ?? '',
        streetAddress: parsedElements?.streetAddress ?? '',
        note: parsedElements?.noteX ?? 'Add a note',
        sire: parsedElements?.statementOfIncomeAndRetainedEarnings ?? '',
        sc: parsedElements?.statementOfCashflow ?? 'Statement of cashflow',
        scse: 'Statement of changes in stockholders equity',
        firmSignature: 'Firm Sign here',
        clientSignature: 'Client Signature',
        clientSignedDate: 'Client Signed Date',
        engId: parsedElements?.clientfirmid ? parsedElements.clientfirmid.toString() : '',
        datePicker: datePicker ? datePicker : 'Date Picker',
        engLetterDate: engLetterDate ? engLetterDate : 'Engagement Letter Date'
      } as AutomatedElements
    } else {
      return new AutomatedElements();
    }
  }

  static mapValuesToReplaceElementWithSpan(elements: AutomatedElements) {
    const replaceElements = new AutomatedElements();
    Object.keys(elements)?.forEach((key) => {
      (replaceElements as AutomatedElements)[key] = elements[key] ? '<span>' + (elements as AutomatedElements)[key] + '</span>' : '';
    });
    return replaceElements;
  }

  static hasOnlyFirmAndClientSignElement = (inputArr: TemplateContentModel[]): boolean => {
    const content = structuredClone(inputArr).filter(s => s.checked).map(s => s.content).join('');
    const firmSignCount = content.match(/<span class="auto-elements is-locked" style="background-color: #DDF0DF;">Client Signature<\/span>/gi);
    const clientSignCount = content.match(/<span class="auto-elements is-locked" style="background-color: #DDF0DF;">Firm Signature<\/span>/gi);
    return !!((firmSignCount && firmSignCount.length === 1) && (clientSignCount && clientSignCount.length === 1));
  };

  static hasOnlyClientSignElement = (inputArr: TemplateContentModel[]): boolean => {
    const content = structuredClone(inputArr).filter(s => s.checked).map(s => s.content).join('');
    const clientSignCount = content.match(/<span class="auto-elements is-locked" style="background-color: #DDF0DF;">Client Signature<\/span>/gi);
    return (clientSignCount && clientSignCount.length === 1);
  };

  static hasOnlyFirmSignElement = (inputArr: TemplateContentModel[]): boolean => {
    const regExpFirm = /<span class="auto-elements is-locked" style="background-color: #DDF0DF;">Firm Signature<\/span>/gi;
    const content = structuredClone(inputArr).filter(s => s.checked).map(s => s.content).join('');
    const firmSignCount = content.match(regExpFirm);
    return (firmSignCount && firmSignCount.length === 1);
  };

  static replaceElementsWithValuesInContent(contentArray: TemplateContentModel[],elements: AutomatedElements, regExp: RegExpModel) {
    const replaceElements: AutomatedElements = this.mapValuesToReplaceElementWithSpan({...elements});
    return this.replaceElementsInContentWithValues([...contentArray], replaceElements, regExp);
  }

  static replaceElementsInContentWithValues(contentArray: TemplateContentModel[], replaceElements: AutomatedElements, regExp: RegExpModel) {
    for (let i = 0; i < contentArray?.length; i++) {
      contentArray[i].sortId = i;
    }
    contentArray?.forEach((item: TemplateContentModel) => {
      if (item?.checked) {
        let targetKey: string;
        let contentString = item.content;
        Object.values(regExp).forEach(value => {
          for (const key in regExp) {
            if (regExp[key] === value) {
              targetKey = key;
              break;
            }
          }
          targetKey = replaceElements[targetKey];
          contentString = contentString.replace(value, targetKey);
        });
        item.content = contentString;
      }
    });
    return structuredClone(contentArray);
  }

  static updateElementsWithSectionLetterType(letter: LetterSectionModel, elements: AutomatedElements) {
    if (letter?.isManagementLetter) {
      return this.updateElementsWithSectionOfMLType(letter, elements);
    } else {
      return this.updateElementsWithSectionOfELType(letter, elements);
    }
  }

  static updateElementsWithSectionOfELType(letter: LetterSectionModel, elements: AutomatedElements) {
    const regExp: RegExpModel = new RegExpModel();
    if(letter?.clientSign?.signedDate) {
      elements.clientSignedDate = letter?.clientSign?.signedDate
    } else {
      delete elements.clientSignedDate;
      delete regExp.clientSignedDate;
    }
    if(letter?.firmSign?.signedDate && letter?.firmSign?.id > 0) {
      elements.firmSignature = letter?.firmSign?.signType != 'text' ?
        this.firmSignatureImageUrl(letter?.firmSign?.fileUrl, letter?.isSent || letter?.isResent) :
        this.firmSignatureTextValue(letter?.firmSign?.signText,letter?.isSent || letter?.isResent);
    } else {
      if (!letter?.isClientAccepted) {
        elements.firmSignature = LETTER_SIGNATURES.FIRM_SIGN_HERE;
      }
    }
    if(letter?.isClientAccepted && letter?.clientSign?.id > 0 ) {
      elements.clientSignature = letter?.clientSign?.signType != 'text' ?
        this.clientSignatureImageUrl(letter?.clientSign?.fileUrl, 'Signed-Url') :
        this.clientSignatureImageUrl(letter?.clientSign?.signText, 'Signed-Text');
      elements.datePicker = elements.datePicker ? elements?.datePicker : 'Date Picker';
      elements.engLetterDate = elements.engLetterDate ? elements.engLetterDate : 'Engagement Letter Date';
    } else {
      elements.datePicker = elements.datePicker ? this.getElementEventValue(false, elements.datePicker) : LETTER_SIGNATURES.DATE_PICKER;
      elements.engLetterDate = elements.engLetterDate ? this.getElementEventValue(true, elements.engLetterDate) : LETTER_SIGNATURES.ENG_LETTER_DATE;
      delete elements.clientSignature;
      delete regExp.clientSignature;
    }

    elements.engLetterDate = elements.engLetterDate ? elements.engLetterDate : LETTER_SIGNATURES.ENG_LETTER_DATE;
    return { elements, regExp };
  }

  static updateElementsWithSectionOfMLType(letter: LetterSectionModel, elements: AutomatedElements) {
    const regExp: RegExpModel = new RegExpModel();
    if (letter?.isClientAccepted && letter?.clientSign?.signedDate && letter?.clientSign?.id > 0) {
      elements.clientSignature = letter?.clientSign?.signType != 'text' ? this.clientSignatureImageUrl(letter?.clientSign?.fileUrl, 'Signed-Url') :
        this.clientSignatureImageUrl(letter?.clientSign?.signText, 'Signed-Text');
      elements.clientSignedDate = letter?.clientSign?.signedDate;
      elements.datePicker = elements.datePicker ? elements?.datePicker : 'Date Picker';
      elements.engLetterDate = elements.engLetterDate ? elements.engLetterDate : 'Engagement Letter Date';
      return {elements, regExp};
    }
    delete regExp.clientSignature;
    delete regExp.clientSignedDate;
    delete elements.clientSignature;
    delete elements.clientSignedDate;
    elements.datePicker = elements.datePicker ? this.getElementEventValue(false, elements?.datePicker) : LETTER_SIGNATURES.DATE_PICKER;
    elements.engLetterDate = elements.engLetterDate ? this.getElementEventValue(true, elements.engLetterDate) : LETTER_SIGNATURES.ENG_LETTER_DATE;
    return {elements, regExp};
  }

  static updateElementsForClientSectionLetterType(letter: LetterSectionModel, elements: AutomatedElements) {
    if (letter?.isManagementLetter) {
      return this.updateElementsWithClientSectionOfMLType(letter, elements);
    } else {
      return this.updateElementsWithClientSectionOfELType(letter, elements);
    }
  }

  static updateElementsWithClientSectionOfELType(letter: LetterSectionModel, elements: AutomatedElements) {
    const regExp: RegExpModel = new RegExpModel();
    if(letter?.clientSign?.signedDate) {
      elements.clientSignedDate = letter?.clientSign?.signedDate
    } else {
      delete elements.clientSignedDate;
      delete regExp.clientSignedDate;
    }
    if(letter?.firmSign?.signedDate && letter?.firmSign?.id > 0) {
      elements.firmSignature = letter?.firmSign?.signType != 'text' ?
        this.firmSignatureImageUrl(letter?.firmSign?.fileUrl, true) :
        this.firmSignatureTextValue(letter?.firmSign?.signText,true);
    }
    if(letter?.isClientAccepted && letter?.clientSign?.id > 0) {
      elements.clientSignature = letter?.clientSign?.signType != 'text' ?
        this.clientSignatureImageUrl(letter?.clientSign?.fileUrl, 'Signed-Url') :
        this.clientSignatureImageUrl(letter?.clientSign?.signText, 'Signed-Text');
    } else {
      elements.clientSignature = this.clientSignatureImageUrl('', letter?.clientSign?.id > 0 ? 'Client_Signed' : 'Client');
    }

    return {elements, regExp};
  }

  static updateElementsWithClientSectionOfMLType(letter: LetterSectionModel, elements: AutomatedElements) {
    const regExp: RegExpModel = new RegExpModel();
    if (letter?.clientSign?.signedDate) {
      elements.clientSignedDate = letter?.clientSign?.signedDate;
    } else {
      delete elements.clientSignedDate;
      delete regExp.clientSignedDate;
    }
    if (letter?.isClientAccepted && letter?.clientSign?.id > 0) {
      elements.clientSignature = letter?.clientSign?.signType != 'text' ?
        this.clientSignatureImageUrl(letter?.clientSign?.fileUrl, 'Signed-Url') :
        this.clientSignatureImageUrl(letter?.clientSign?.signText, 'Signed-Text');
    } else {
      elements.clientSignature = this.clientSignatureImageUrl('', letter?.clientSign?.id > 0 ? 'Client_Signed' : 'Client');
    }

    return { elements, regExp };
  }

  static firmSignatureImageUrl = (url: string, isChangeable: boolean): string => {
    return isChangeable ? `<img src="${url}" alt="Firm Signature Image">` :
      `<img src="${url}" alt="Firm Signature Image"><br><a class="cursor-pointer mt-2 button-small mat-button
                                  mat-flat-button mat-primary mat-success element-event-firm-sign">Change signature</a>`;
  }
  static firmSignatureTextValue = (url: string, isChangeable: boolean): string => {
    return isChangeable ? `<span class="font-sign fs-50">${url}</span>` :
      `<span class="font-sign fs-50">${url}</span><br><a class="mt-2 button-small mat-button
                                  mat-flat-button mat-primary mat-success element-event-firm-sign">Change signature</a>`;
  }

  static clientSignatureImageUrl = (url: string, letterView: 'Firm' | 'Client' | 'Client_Signed' | 'Signed-Url' | 'Signed-Text'): string => {
    switch (letterView) {
      case 'Firm':
        return LETTER_SIGNATURES.CLIENT_SIGN_HERE;
      case 'Client':
        return LETTER_SIGNATURES.CLIENT_SIGN_HERE;
      case 'Client_Signed':
        return LETTER_SIGNATURES.CHANGE_CLIENT_SIGN;
      case 'Signed-Url':
        return `<img src="${url}" alt="Client Signature Image">`;
      case 'Signed-Text':
        return `<span class="font-sign fs-50">${url}</span>`;
    }
  }

  static prepareStatusInfo(letter: LetterSectionModel) {
    if(!letter){ return null}
    const status : LetterShareStatus = new LetterShareStatus();
    status.hasSigned = letter?.firmSign?.id > 0 ;
    status.hasSent = status.hasSigned ? (status.hasSigned && letter?.isSent) : letter?.isSent;
    status.hasResent = status.hasSent && letter?.isResent;
    status.sharedDate = status.hasResent ?  letter?.resentDate :  letter?.sentDate;
    status.status = letter?.isClientAccepted ? 'Approved on' : (status?.hasResent ? 'Reshared on' : 'Shared on');
    status.statusTooltip = `Shared on Client Portal by ${status.hasResent ? letter?.reSentByUserName : letter?.sentByUserName}`;
    status.userInitials = GenerateRandomUtils.getInitials(status.hasResent ?  letter?.reSentByUserName :  letter?.sentByUserName);
    status.isClientAccepted = letter?.isClientAccepted;
    status.clientApprovedAt = letter?.clientSignoffDate;
    return status;
  }

  static validImageItem(imageHeight: any, imageWidth: any, from: CLIENT_IMAGES_FOLDERS): boolean {
    switch (from) {
      case 'Signature':
        return !(imageWidth > 201 || imageHeight > 126);
      case 'Header':
        return !(imageWidth > 721 || imageHeight > 201);
      case 'Footer':
        return !(imageWidth > 721 || imageHeight > 71);

    }
  }

  static getContentForSaving(edited: LetterS3Model, original: LetterS3Model, letter: LetterSectionModel): string {
    if (letter?.firmSign?.id > 0) {
      if (letter?.isSent || letter?.isResent) {
        edited.isLetterEditedAfterSend = true;
        edited.contentBeforeEdit = [...(original?.isLetterEditedAfterSend ? original?.contentBeforeEdit : original?.content)];
      } else {
        edited.isLetterEditedAfterSend = false;
        edited.contentBeforeEdit = [];
      }
    } else {

    }
    return JSON.stringify({...edited});
  }


  static setTemplateAccess(templateEngTypeId: number = 0, sourceEngTypeId: number = 0, hasReview: boolean = false) {
    if (templateEngTypeId === 0) {
      return false;
    }
    if (!hasReview && templateEngTypeId == 2) {
      return true;
    }
    switch (sourceEngTypeId) {
      case 1:
        return ![1, 34, 35, 36, 37].includes(templateEngTypeId);
      case 2:
        return ![2, 34, 35, 36, 37].includes(templateEngTypeId);
      case 34:
      case 35:
      case 36:
      case 37:
        return ![1, 34, 35, 36, 37].includes(templateEngTypeId);
      default:
        return true;
    }
  }

  static canEmailBeShared(letter: LetterSectionModel) {
    return !!(letter?.manualAcceptedDate || letter?.isSent || letter?.isResent || letter?.isClientAccepted);
  }

  static isValidFileCheck(fileName: string = '') {
    return fileName.endsWith('.jpeg') || fileName.endsWith('.jpg') || fileName.endsWith('.png') || fileName.endsWith('.gif');
  }

  static getFirmSignAndType(letter: LetterSectionModel): { firmSign: string | null, firmSignType: string | null } {
    if (letter?.firmSign?.id > 0) {
      return {
        firmSign: (letter?.firmSign?.signType != 'text' ? letter?.firmSign?.filePath : letter?.firmSign?.signText)
        , firmSignType: letter?.firmSign?.signType
      };
    }
    return {firmSign: null, firmSignType: null};
  }

  static getClientSignAndType(letter: LetterSectionModel): {
    clientSign: string | null,
    clientSignType: string | null
  } {
    if (letter?.clientSign?.id > 0) {
      return {
        clientSign: (letter?.clientSign?.signType != 'text' ? letter?.clientSign?.filePath : letter?.clientSign?.signText)
        , clientSignType: letter?.clientSign?.signType
      };
    }
    return {clientSign: null, clientSignType: null};
  }

  static getHeaderAndFooterName(header: ImageLetterModel, footer: ImageLetterModel): {
    headerName: string | null,
    footerName: string | null
  } {
    const allowedExt = ['jpg', 'gif', 'png', 'jpeg'];
    const headerFileExt = header?.s3FilePath ? header?.s3FilePath?.split('.').pop() : 'jpg';
    const footerFileExt = footer?.s3FilePath ? footer?.s3FilePath?.split('.').pop() : 'jpg';
    return {
      headerName: `${header?.imageId}.${headerFileExt ? allowedExt?.includes(headerFileExt) : 'jpg'}`,
      footerName: `${footer?.imageId}.${footerFileExt ? allowedExt?.includes(footerFileExt) : 'jpg'}`
    };
  }

  static getHeaderAndFooterConfig(config: ComponentConfigModel): {
    headerConfig: string | null,
    footerConfig: string | null
  } {
    return {
      headerConfig: config?.hasNoHeaderFooter ? CONFIG_OPTIONS.NONE : config?.headerConfig,
      footerConfig: config?.hasNoHeaderFooter ? CONFIG_OPTIONS.NONE : config?.footerConfig
    };
  }

  static getFileName(isPDF: boolean = false, engName: string = '', sectionName: string = ''): string {
    return `${engName}-${sectionName?.split(' ')?.join('-')}${isPDF ? '.pdf' : '.docx'}`;
  }

  static getManualLetterName(engName: string, sectionName: string) {
    return `${engName}_${sectionName?.split(' ')?.join('_')}.pdf`;
  }

  static findEmptyAutomatedElementsValues(elements: AutomatedElements, letterContent: TemplateContentModel[]) {
    const emptyElements: string[] = [];
    const replaceElements: AutomatedElements = new AutomatedElements();
    const elementValues = {...elements} as AutomatedElements;
    for (const key in elementValues) {
      if (elements?.hasOwnProperty(key)) {
        const isEmpty = this.checkEmptyValue(key, elementValues);
        if (isEmpty) {
          const emptyElement = replaceElements[key];
          emptyElements.push(emptyElement);
        }
      }
    }
    const content = letterContent?.filter(l => l?.checked)?.map(l => l?.content)?.join('');
    const isIncluded: string[] = [];
    emptyElements?.forEach((elem, key) => {
      if (content?.includes(elem)) {
        isIncluded.push(elem);
      }
    });
    return isIncluded;
  }

  static checkEmptyValue(key: string, data: AutomatedElements) {
    if (data[key] === null || data[key] === '' || data[key] === ' ') {
      if (key === 'date') {
        return false;
      }
      if ((key == 'cityState')) {
        if (data['cityProvince']?.split(',')?.length > 1 || key === 'cityState') {
          return false;
        }
      }
      return true;
    } else {
      if (key == 'addComplete') {
        const regExp = /[a-zA-Z]/g;
        data['addComplete'] = data['addComplete']?.replace(/<br\s*\/?>/gi, '');
        if (regExp.test(data['addComplete'])) {
          return false;
        } else {
          return true;
        }
      }
      if (key === 'firmAddress') {
        const regExp = /[a-zA-Z]/g;
        data['firmAddress'] = data['firmAddress']?.replace(/<br\s*\/?>/gi, '');
        if (regExp.test(data['firmAddress'])) {
          return false;
        } else {
          return true;
        }
      }
    }
  }

  static getCurrentFormattedDate(): string {
    const date = new Date(); // today's date
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0'); // 0-based month
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  }

  static getElementEventValue(isEngLetterElement: boolean = false, value: string = '') {
    if (isEngLetterElement) {
      return `<a class="mt-2 button-small mat-button mat-stroked-button mat-primary text-gray-4
            fw-regular gap-4 cursor-pointer element-event-date-letter">${value ? value : 'Engagement Letter Date'}<i class="fa-regular fa-calendar element-event-date-letter"></i></a>`;
    }
    return `<a class="mt-2 button-small mat-button mat-stroked-button mat-primary text-gray-4
            fw-regular gap-4 cursor-pointer element-event-date-picker">${value ? value : 'Date Picker'}<i class="fa-regular fa-calendar element-event-date-picker"></i></a>`;
  }

  static setEngLetterDateElementInfo(engLetterInfo: Record<string, any>): Record<string, any> {
    return {
      subModuleId: engLetterInfo?.subModuleId,
      signOffDate: engLetterInfo?.signOffDate,
      engagementTemplateId: engLetterInfo?.engagementTemplateId
    };
  }

}
